import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Fighter, ProductMatch } from '../utils/types';
import { motion, AnimatePresence } from 'framer-motion';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const fightersData: Fighter[] = require('../utils/fighters.json');
const matchesData: ProductMatch[] = require('../utils/matches.json');

// Image cache to track loaded images
const imageCache = new Set<string>();

// Cached Image component - shows thumb only (full loaded on zoom)
const CachedImage: React.FC<{
    src: string;
    alt: string;
    className?: string;
}> = ({ src, alt, className }) => {
    const isAlreadyCached = imageCache.has(src);
    const [isLoaded, setIsLoaded] = useState(isAlreadyCached);
    const [hasError, setHasError] = useState(false);
    const [showSkeleton, setShowSkeleton] = useState(false);

    // Only show skeleton after delay if image hasn't loaded yet
    useEffect(() => {
        if (isLoaded || isAlreadyCached) return;
        const timer = setTimeout(() => {
            if (!isLoaded) setShowSkeleton(true);
        }, 300);
        return () => clearTimeout(timer);
    }, [isLoaded, isAlreadyCached]);

    const handleLoad = useCallback(() => {
        imageCache.add(src);
        setIsLoaded(true);
        setShowSkeleton(false);
    }, [src]);

    const handleError = useCallback(() => {
        setHasError(true);
        setShowSkeleton(false);
    }, []);

    if (hasError) {
        return (
            <div className={`${className} flex items-center justify-center bg-gray-100 dark:bg-gray-600`}>
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            </div>
        );
    }

    return (
        <>
            {showSkeleton && !isLoaded && (
                <div className={`${className} animate-pulse bg-gray-100 dark:bg-gray-600 rounded absolute inset-0`} />
            )}
            <img
                src={src}
                alt={alt}
                className={`${className} transition-opacity duration-200 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                loading="lazy"
                decoding="async"
                onLoad={handleLoad}
                onError={handleError}
            />
        </>
    );
};

type ThemeMode = 'light' | 'dark' | 'system';

const MatchFightersPage: React.FC = () => {
    const [selectedFighter, setSelectedFighter] = useState<Fighter | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
        // Restore theme from localStorage
        const saved = localStorage.getItem('themeMode');
        return (saved as ThemeMode) || 'system';
    });
    const [scrollPosition, setScrollPosition] = useState(0);
    const [isScanning, setIsScanning] = useState(false);
    const [scanningFighter, setScanningFighter] = useState<Fighter | null>(null);
    const [scanProgress, setScanProgress] = useState(0);
    const [zoomedImage, setZoomedImage] = useState<string | null>(null);
    const fightersContainerRef = useRef<HTMLDivElement>(null);

    // Touch/drag state for mobile carousel
    const [isDragging, setIsDragging] = useState(false);
    const [hasDragged, setHasDragged] = useState(false);

    // Theme handling + persist to localStorage
    useEffect(() => {
        const root = document.documentElement;
        // Save to localStorage
        localStorage.setItem('themeMode', themeMode);

        if (themeMode === 'system') {
            const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            root.classList.toggle('dark', systemDark);
        } else {
            root.classList.toggle('dark', themeMode === 'dark');
        }
    }, [themeMode]);

    // Listen for system theme changes
    useEffect(() => {
        if (themeMode !== 'system') return;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => {
            document.documentElement.classList.toggle('dark', e.matches);
        };
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, [themeMode]);

    const filteredFighters = useMemo(() => {
        if (!searchQuery.trim()) {
            return fightersData;
        }
        return fightersData.filter(fighter =>
            fighter.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    const filteredProducts = useMemo(() => {
        if (!selectedFighter) {
            return matchesData;
        }
        // Filter and sort by confidence (highest first - descending)
        const filtered = matchesData.filter(product =>
            product.matches.some(match => match.name === selectedFighter.id)
        );

        return [...filtered].sort((a, b) => {
            const aMatch = a.matches.find(m => m.name === selectedFighter.id);
            const bMatch = b.matches.find(m => m.name === selectedFighter.id);
            const aConfidence = aMatch?.confidence ?? 0;
            const bConfidence = bMatch?.confidence ?? 0;
            return bConfidence - aConfidence; // Descending: highest first
        });
    }, [selectedFighter]);

    const totalProducts = filteredProducts.length;

    const handleFighterClick = (fighter: Fighter) => {
        // Prevent selection if user was just dragging
        if (hasDragged) return;

        if (selectedFighter?.id === fighter.id) {
            setSelectedFighter(null);
            setScanningFighter(null);
        } else {
            // Start scanning animation
            setIsScanning(true);
            setScanningFighter(fighter);
            setScanProgress(0);

            // Preload matching product images during scanning
            const matchingProducts = matchesData.filter(product =>
                product.matches.some(match => match.name === fighter.id)
            );

            // Preload thumbnails only (originals loaded on zoom)
            matchingProducts.forEach((product, index) => {
                // Stagger preloading to avoid overwhelming the browser
                setTimeout(() => {
                    const thumbImg = new Image();
                    thumbImg.onload = () => {
                        imageCache.add(product.thumb_url || product.image_url);
                    };
                    thumbImg.src = product.thumb_url || product.image_url;
                }, index * 50); // 50ms stagger between each image
            });

            // Simulate scanning progress
            const duration = 2000; // 2 seconds
            const interval = 50;
            const steps = duration / interval;
            let currentStep = 0;

            const progressInterval = setInterval(() => {
                currentStep++;
                setScanProgress((currentStep / steps) * 100);

                if (currentStep >= steps) {
                    clearInterval(progressInterval);
                    setIsScanning(false);
                    setSelectedFighter(fighter);
                }
            }, interval);
        }
    };

    const scrollFighters = (direction: 'left' | 'right') => {
        if (!fightersContainerRef.current) return;
        const container = fightersContainerRef.current;
        const cardWidth = 180; // card width + gap
        const newPosition = direction === 'left'
            ? Math.max(0, scrollPosition - cardWidth)
            : scrollPosition + cardWidth;
        container.scrollTo({ left: newPosition, behavior: 'smooth' });
        setScrollPosition(newPosition);
    };

    const handleScroll = () => {
        if (fightersContainerRef.current) {
            setScrollPosition(fightersContainerRef.current.scrollLeft);
        }
    };

    const dragStartXRef = useRef(0);
    const dragScrollLeftRef = useRef(0);
    const totalDragDistanceRef = useRef(0);

    const handleTouchStart = (e: React.TouchEvent) => {
        if (!fightersContainerRef.current) return;
        setIsDragging(true);
        setHasDragged(false);
        totalDragDistanceRef.current = 0;
        dragStartXRef.current = e.touches[0].pageX;
        dragScrollLeftRef.current = fightersContainerRef.current.scrollLeft;
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        // Only consider it a drag if moved more than 10px total
        if (totalDragDistanceRef.current > 10) {
            setHasDragged(true);
            // Reset hasDragged after a short delay to allow click events
            setTimeout(() => setHasDragged(false), 100);
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging || !fightersContainerRef.current) return;

        const currentX = e.touches[0].pageX;
        const diff = dragStartXRef.current - currentX;

        totalDragDistanceRef.current += Math.abs(currentX - (dragStartXRef.current - totalDragDistanceRef.current));

        fightersContainerRef.current.scrollLeft = dragScrollLeftRef.current + diff;
    };

    // Recalculate scroll state when fighters list changes or on mount
    const [hasOverflow, setHasOverflow] = useState(false);

    useEffect(() => {
        const checkOverflow = () => {
            if (fightersContainerRef.current) {
                const hasScroll = fightersContainerRef.current.scrollWidth > fightersContainerRef.current.clientWidth;
                setHasOverflow(hasScroll);
            }
        };

        // Check on mount and when fighters change
        checkOverflow();
        // Reset scroll position when filter changes
        if (fightersContainerRef.current) {
            fightersContainerRef.current.scrollLeft = 0;
            setScrollPosition(0);
        }

        // Also check on window resize
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [filteredFighters.length]);

    const canScrollLeft = hasOverflow && scrollPosition > 0;
    const canScrollRight = hasOverflow && fightersContainerRef.current
        ? scrollPosition < fightersContainerRef.current.scrollWidth - fightersContainerRef.current.clientWidth - 1
        : false;

    const getConfidenceColor = (confidence: number) => {
        if (confidence < 0.5) return { bar: '#ef4444', bg: '#fecaca' }; // red
        if (confidence < 0.7) return { bar: '#f59e0b', bg: '#fef3c7' }; // yellow/orange
        return { bar: '#22c55e', bg: '#dcfce7' }; // green
    };

    return (
        <div className="min-h-screen bg-[#F9F8FD] dark:bg-gray-900 flex flex-col transition-colors duration-300">
            <div className="max-w-[1400px] w-full mx-auto flex flex-col h-screen px-4 md:px-6">
                {/* Fighters Section - Sticky */}
                <motion.div
                    className="sticky top-0 z-10 bg-white dark:bg-gray-800 rounded-[14px] shadow-sm p-4 md:p-5 mt-4"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {/* Header with title and theme toggle */}
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                        <h2 className="text-base font-bold text-black dark:text-white">Fighters</h2>

                        {/* Theme Toggle */}
                        <div className="flex bg-[#F6F6F9] dark:bg-gray-700 rounded-full p-1">
                            <button
                                onClick={() => setThemeMode('light')}
                                className={`p-1.5 rounded-full transition-colors ${
                                    themeMode === 'light' ? 'bg-white dark:bg-gray-600 text-blue-600 shadow-sm' : 'text-gray-500 dark:text-gray-400'
                                }`}
                                title="Light mode"
                            >
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                                </svg>
                            </button>
                            <button
                                onClick={() => setThemeMode('system')}
                                className={`p-1.5 rounded-full transition-colors ${
                                    themeMode === 'system' ? 'bg-white dark:bg-gray-600 text-blue-600 shadow-sm' : 'text-gray-500 dark:text-gray-400'
                                }`}
                                title="System mode"
                            >
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.321A1 1 0 0113 17H7a1 1 0 01-.293-1.953l.804-.32.122-.49H5a2 2 0 01-2-2V5zm12 0H5v8h10V5z" clipRule="evenodd" />
                                </svg>
                            </button>
                            <button
                                onClick={() => setThemeMode('dark')}
                                className={`p-1.5 rounded-full transition-colors ${
                                    themeMode === 'dark' ? 'bg-white dark:bg-gray-600 text-blue-600 shadow-sm' : 'text-gray-500 dark:text-gray-400'
                                }`}
                                title="Dark mode"
                            >
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Search Input */}
                    <div className="mb-4 md:mb-5">
                        <div className="relative">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2">
                                <svg className="w-4 h-4 text-black dark:text-white" fill="none" viewBox="0 0 14 14">
                                    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5"/>
                                    <path d="M10 10L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full h-10 pl-10 pr-10 bg-[#F6F6F9] dark:bg-gray-700 border border-[#ECECEE] dark:border-gray-600 rounded text-sm text-black dark:text-white placeholder:text-black/50 dark:placeholder:text-white/50 focus:outline-none focus:border-blue-500"
                            />
                            {/* Clear search button */}
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
                                >
                                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Fighters Grid with Arrows */}
                    <div className="relative">
                        {/* Left Arrow - Mobile only */}
                        <button
                            onClick={() => scrollFighters('left')}
                            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:hidden flex items-center justify-center bg-white dark:bg-gray-700 rounded-full shadow-md transition-opacity ${
                                canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
                            }`}
                        >
                            <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        {/* Fighters Container */}
                        <div
                            ref={fightersContainerRef}
                            onScroll={handleScroll}
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                            onTouchMove={handleTouchMove}
                            className="fighters-scroll flex gap-3 md:gap-4 overflow-x-auto pb-2 md:pb-3 md:scroll-smooth"
                        >
                            {filteredFighters.map((fighter) => (
                                <motion.div
                                    key={fighter.id}
                                    className={`flex-shrink-0 w-[140px] md:w-[172px] h-[120px] md:h-[146px] rounded-[17px] cursor-pointer transition-all duration-200 bg-[#F9F8FD] dark:bg-gray-700 ${
                                        selectedFighter?.id === fighter.id
                                            ? 'border-[3px] border-[#176BFB] shadow-md'
                                            : 'border-[3px] border-transparent hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
                                    }`}
                                    onClick={() => handleFighterClick(fighter)}
                                >
                                    <div className="h-full flex flex-col items-center justify-center gap-2">
                                        <img
                                            src={fighter.img_url}
                                            alt={fighter.name}
                                            className="w-[100px] md:w-[140px] h-[70px] md:h-[100px] object-contain"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                        <span className="text-[11px] md:text-[13px] text-black dark:text-white text-center leading-tight px-2">
                                            {fighter.name}
                                        </span>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Right Arrow - Mobile only */}
                        <button
                            onClick={() => scrollFighters('right')}
                            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:hidden flex items-center justify-center bg-white dark:bg-gray-700 rounded-full shadow-md transition-opacity ${
                                canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
                            }`}
                        >
                            <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                </motion.div>

                {/* Products Section - Scrollable */}
                <div className="flex-1 overflow-hidden mt-4 md:mt-5 mb-4 md:mb-6">
                    <motion.div
                        className="bg-white dark:bg-gray-800 rounded-[14px] shadow-sm h-full flex flex-col"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                    >
                        {/* Products Header */}
                        <div className="p-4 md:p-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <h2 className="text-base font-bold text-black dark:text-white">Products</h2>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    ({totalProducts} total)
                                </span>
                            </div>
                            {/* Clear filter button */}
                            {selectedFighter && (
                                <button
                                    onClick={() => {
                                        setSelectedFighter(null);
                                        setScanningFighter(null);
                                    }}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-[#176BFB]/10 hover:bg-[#176BFB]/20 text-[#176BFB] rounded-full text-xs font-medium transition-colors"
                                >
                                    <span>{selectedFighter.name}</span>
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* Products Grid - Scrollable */}
                        <div className="flex-1 overflow-y-auto p-4 md:p-5 relative">
                            {/* Scanning Overlay - Fixed to viewport */}
                            <AnimatePresence>
                                {isScanning && scanningFighter && (
                                    <motion.div
                                        className="fixed inset-0 z-50 bg-black/60 dark:bg-black/80 flex flex-col items-center justify-center backdrop-blur-sm"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                    >
                                        {/* Fighter face being scanned */}
                                        <motion.div
                                            className="relative mb-6"
                                            initial={{ scale: 0.5, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-white/30 relative">
                                                <img
                                                    src={scanningFighter.img_url}
                                                    alt={scanningFighter.name}
                                                    className="w-full h-full object-cover"
                                                />
                                                {/* Scanning line animation */}
                                                <motion.div
                                                    className="absolute left-0 right-0 h-1 bg-[#176BFB] shadow-[0_0_20px_#176BFB]"
                                                    initial={{ top: 0 }}
                                                    animate={{ top: ['0%', '100%', '0%'] }}
                                                    transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                                                />
                                            </div>
                                            {/* Pulse rings */}
                                            <motion.div
                                                className="absolute inset-0 rounded-full border-2 border-[#176BFB]"
                                                initial={{ scale: 1, opacity: 0.8 }}
                                                animate={{ scale: 1.5, opacity: 0 }}
                                                transition={{ duration: 1, repeat: Infinity }}
                                            />
                                            <motion.div
                                                className="absolute inset-0 rounded-full border-2 border-[#176BFB]"
                                                initial={{ scale: 1, opacity: 0.8 }}
                                                animate={{ scale: 1.8, opacity: 0 }}
                                                transition={{ duration: 1, repeat: Infinity, delay: 0.3 }}
                                            />
                                        </motion.div>

                                        {/* Fighter name */}
                                        <motion.p
                                            className="text-white text-lg md:text-xl font-semibold mb-4"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}
                                        >
                                            {scanningFighter.name}
                                        </motion.p>

                                        {/* Scanning text */}
                                        <motion.p
                                            className="text-white/70 text-sm mb-4"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.3 }}
                                        >
                                            Scanning products for face match...
                                        </motion.p>

                                        {/* Progress bar */}
                                        <div className="w-48 md:w-64 h-2 bg-white/20 rounded-full overflow-hidden">
                                            <motion.div
                                                className="h-full bg-[#176BFB] rounded-full"
                                                style={{ width: `${scanProgress}%` }}
                                                transition={{ duration: 0.1 }}
                                            />
                                        </div>
                                        <p className="text-white/50 text-xs mt-2">{Math.round(scanProgress)}%</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                                {filteredProducts.map((product, index) => {
                                    const fighterMatch = selectedFighter
                                        ? product.matches.find(m => m.name === selectedFighter.id)
                                        : null;
                                    const confidence = fighterMatch?.confidence ?? (product.matches.length > 0 ? Math.max(...product.matches.map(m => m.confidence)) : 0);
                                    const confidencePercent = Math.round(confidence * 100);
                                    const colors = getConfidenceColor(confidence);

                                    return (
                                        <div
                                            key={`product-${product.image_url}-${index}`}
                                            className="aspect-square bg-white dark:bg-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer relative group"
                                            onClick={() => setZoomedImage(product.image_url)}
                                        >
                                            <div className={`w-full p-3 md:p-4 ${selectedFighter ? 'h-[calc(100%-20px)]' : 'h-full'} relative`}>
                                                <CachedImage
                                                    src={product.thumb_url || product.image_url}
                                                    alt="Product"
                                                    className="w-full h-full object-contain"
                                                />
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none">
                                                    <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                    </svg>
                                                </div>
                                            </div>
                                            {selectedFighter && (
                                                <div className="absolute bottom-0 left-0 right-0 h-5 bg-gray-50 dark:bg-gray-600 px-2 flex items-center gap-1">
                                                    <span className="text-[8px] md:text-[9px] font-semibold text-gray-500 dark:text-gray-400">
                                                        Confidence
                                                    </span>
                                                    <div
                                                        className="flex-1 h-1.5 rounded-full overflow-hidden"
                                                        style={{ backgroundColor: colors.bg }}
                                                    >
                                                        <div
                                                            className="h-full rounded-full transition-all duration-700"
                                                            style={{ backgroundColor: colors.bar, width: `${confidencePercent}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-[9px] md:text-[10px] font-medium text-gray-600 dark:text-gray-300 min-w-[28px] text-right">
                                                        {confidencePercent}%
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {totalProducts === 0 && (
                                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                                    No products found for this fighter
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Zoom Modal */}
            <AnimatePresence>
                {zoomedImage && (
                    <motion.div
                        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setZoomedImage(null)}
                    >
                        {/* Close button */}
                        <button
                            className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                            onClick={() => setZoomedImage(null)}
                        >
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Zoom controls info */}
                        <div className="absolute top-4 left-4 z-10 text-white/70 text-sm hidden md:block">
                            <p>Scroll to zoom • Drag to pan • Double-click to reset</p>
                        </div>
                        <div className="absolute top-4 left-4 z-10 text-white/70 text-xs md:hidden">
                            <p>Pinch to zoom • Drag to pan</p>
                        </div>

                        {/* Zoomable image */}
                        <div
                            className="w-full h-full flex items-center justify-center p-4"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <TransformWrapper
                                initialScale={1}
                                minScale={0.5}
                                maxScale={5}
                                centerOnInit={true}
                                doubleClick={{ mode: 'reset' }}
                                wheel={{ step: 0.2 }}
                            >
                                {({ zoomIn, zoomOut, resetTransform }) => (
                                    <>
                                        {/* Zoom controls */}
                                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2 bg-white/10 backdrop-blur-sm rounded-full p-1">
                                            <button
                                                onClick={() => zoomOut()}
                                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                                title="Zoom out"
                                            >
                                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => resetTransform()}
                                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                                title="Reset zoom"
                                            >
                                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => zoomIn()}
                                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                                title="Zoom in"
                                            >
                                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                </svg>
                                            </button>
                                        </div>
                                        <TransformComponent
                                            wrapperStyle={{
                                                width: '100%',
                                                height: '100%',
                                            }}
                                            contentStyle={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <img
                                                src={zoomedImage}
                                                alt="Zoomed product"
                                                className="max-w-full max-h-[85vh] object-contain"
                                            />
                                        </TransformComponent>
                                    </>
                                )}
                            </TransformWrapper>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MatchFightersPage;
